import { useState, useRef, useCallback, useEffect } from 'react'
import { wsService } from '@/services/websocket'

export type VoiceState = 'idle' | 'connecting' | 'listening' | 'processing' | 'speaking' | 'error'

interface UseVoiceOptions {
  onTranscript?: (text: string) => void
  onResponse?: (text: string) => void
  onError?: (error: string) => void
  /** Callback to add a user message to the chat */
  onAddUserMessage?: (content: string) => string | void
  /** Callback to update a message status */
  onUpdateMessageStatus?: (messageId: string, status: 'sending' | 'sent' | 'delivered' | 'error') => void
  /** Current conversation ID */
  conversationId?: string | null
}

// Extend window for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message: string
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition
    webkitSpeechRecognition: new () => SpeechRecognition
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

export function useVoice(options: UseVoiceOptions = {}) {
  const [state, setState] = useState<VoiceState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [transcript, setTranscript] = useState('')
  const [isSupported, setIsSupported] = useState(true)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)
  const pendingMessageIdRef = useRef<string | null>(null)
  const finalTranscriptRef = useRef<string>('')

  // Check browser support for Web Speech API
  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition
    setIsSupported(!!SpeechRecognitionAPI)
  }, [])

  // Listen for audio responses from WebSocket
  useEffect(() => {
    const handleChat = (message: { data: { audio_base64?: string; content?: string; role?: string; status?: string } }) => {
      // Only handle assistant responses with audio
      if (message.data.role === 'assistant' && message.data.status !== 'received') {
        // Close overlay immediately so user can see the chat response
        // Audio will play in the background
        setState('idle')
        setTranscript('')
        
        // Play the audio response if available (in background)
        if (message.data.audio_base64) {
          playAudioInBackground(message.data.audio_base64)
        }
        
        // Call response callback
        if (message.data.content) {
          options.onResponse?.(message.data.content)
        }
        
        // Update pending user message status
        if (pendingMessageIdRef.current && options.onUpdateMessageStatus) {
          options.onUpdateMessageStatus(pendingMessageIdRef.current, 'delivered')
          pendingMessageIdRef.current = null
        }
      } else if (message.data.role === 'user' && message.data.status === 'received') {
        // User message acknowledged
        if (pendingMessageIdRef.current && options.onUpdateMessageStatus) {
          options.onUpdateMessageStatus(pendingMessageIdRef.current, 'sent')
        }
      }
    }

    const unsubscribe = wsService.on('chat', handleChat)
    return () => unsubscribe()
  }, [options])

  // Play audio in background (doesn't block UI)
  const playAudioInBackground = useCallback((base64Audio: string) => {
    try {
      const audioData = atob(base64Audio)
      const arrayBuffer = new ArrayBuffer(audioData.length)
      const view = new Uint8Array(arrayBuffer)
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i)
      }
      const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' })
      const url = URL.createObjectURL(blob)

      // Stop any currently playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause()
        currentAudioRef.current = null
      }

      const audio = new Audio(url)
      currentAudioRef.current = audio

      audio.onended = () => {
        URL.revokeObjectURL(url)
        currentAudioRef.current = null
      }
      audio.onerror = () => {
        URL.revokeObjectURL(url)
        currentAudioRef.current = null
      }
      audio.play().catch((err) => {
        console.error('[Voice] Audio playback failed:', err)
      })
    } catch (error) {
      console.error('[Voice] Failed to decode/play audio:', error)
    }
  }, [])

  const startListening = useCallback(async () => {
    if (!isSupported) {
      setError('Voice is not supported in this browser')
      return
    }

    try {
      setState('connecting')
      setError(null)
      setTranscript('')
      finalTranscriptRef.current = ''

      // Use Web Speech API (free, browser-based)
      const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognition = new SpeechRecognitionAPI()
      recognitionRef.current = recognition

      recognition.continuous = false  // Stop after first final result
      recognition.interimResults = true  // Show live transcript while speaking
      recognition.lang = 'en-US' // Default to English, can be made configurable

      recognition.onstart = () => {
        console.log('[Voice] Web Speech API started')
        setState('listening')
      }

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        // Get the latest result
        const lastResultIndex = event.results.length - 1
        const result = event.results[lastResultIndex]
        const transcript = result[0].transcript

        if (result.isFinal) {
          // Final result - store it
          finalTranscriptRef.current = transcript.trim()
          setTranscript(transcript.trim())
          console.log('[Voice] Final transcript:', transcript.trim())
        } else {
          // Interim result - show live update
          setTranscript(transcript)
          console.log('[Voice] Interim:', transcript)
        }
      }

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('[Voice] Speech recognition error:', event.error)
        if (event.error === 'no-speech') {
          setError('No speech detected. Please try again.')
        } else if (event.error === 'audio-capture') {
          setError('Microphone not available.')
        } else if (event.error === 'not-allowed') {
          setError('Microphone access denied.')
        } else {
          setError(`Error: ${event.error}`)
        }
        setState('error')
      }

      recognition.onend = () => {
        console.log('[Voice] Speech recognition ended')
        // Process final transcript if we have one
        const finalText = finalTranscriptRef.current.trim()
        if (finalText && state === 'listening') {
          processTranscript(finalText)
        } else if (!finalText) {
          setState('idle')
        }
      }

      // Request microphone permission and start
      recognition.start()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start voice'
      setError(message)
      setState('error')
      options.onError?.(message)
    }
  }, [isSupported, options, state])

  const stopListening = useCallback(() => {
    // Stop speech recognition
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }

    // Stop any playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause()
      currentAudioRef.current = null
    }

    // Process transcript if we have one
    const finalText = finalTranscriptRef.current.trim()
    if (finalText && state === 'listening') {
      processTranscript(finalText)
    } else {
      setState('idle')
      setTranscript('')
    }
  }, [state])

  const processTranscript = useCallback((userText: string) => {
    setState('processing')
    console.log('[Voice] Processing transcript:', userText)

    options.onTranscript?.(userText)

    // Add user message to chat if callback provided
    if (options.onAddUserMessage) {
      const messageId = options.onAddUserMessage(`🎤 ${userText}`)
      if (messageId) {
        pendingMessageIdRef.current = messageId
      }
    }

    // Send to Niki via WebSocket with voice flag
    console.log('[Voice] Sending to Niki via WebSocket...')
    wsService.sendChat(userText, options.conversationId || undefined, undefined, {
      isVoiceInput: true,
      voice: 'rachel', // ElevenLabs voice (not OpenAI's 'nova')
    })
  }, [options])

  return {
    state,
    error,
    transcript,
    isSupported,
    isListening: state === 'listening',
    isProcessing: state === 'processing',
    isSpeaking: state === 'speaking',
    startListening,
    stopListening,
  }
}
